'use client'

import {
    CSSProperties,
    ReactNode,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Dropdown que não é cortado pelo contêiner.
 *
 * **O problema:** dropdown posicionado com `absolute` vive dentro do fluxo do pai. Se
 * qualquer ancestral tem `overflow-x-auto` — toda tabela larga do sistema tem —, o
 * navegador passa a tratar o eixo Y como área de rolagem também (`overflow-y: visible`
 * vira `auto` quando o outro eixo não é visível). Resultado: a lista de opções é cortada
 * na borda da tabela e o operador não consegue escolher. Isso já apareceu três vezes:
 * componentes do pacote, materiais do orçamento e destinatário do OPME.
 *
 * **A solução:** o painel sai do fluxo (portal no `body`) e é posicionado com `fixed`,
 * ancorado no campo. Fora do contêiner, não há o que o corte.
 *
 * Dois cuidados que o portal exige e que são fáceis de esquecer:
 * 1. **Reposicionar ao rolar/redimensionar** — `fixed` não acompanha o campo sozinho. O
 *    listener de scroll é capturado (`true`) para pegar rolagem de QUALQUER ancestral,
 *    não só da janela.
 * 2. **Clique fora** — o painel não está mais dentro do container, então o handler que
 *    fecha no `mousedown` precisa considerar os dois elementos. Sem isso, clicar numa
 *    opção fecha o dropdown antes de o `click` chegar ao botão, e a escolha se perde.
 */
export interface AnchoredDropdownOptions {
    /**
     * Copiar a LARGURA do elemento âncora (padrão: `true`).
     *
     * Verdadeiro serve para select: a lista tem a largura do campo. Falso serve para
     * painel disparado por BOTÃO — um botão de ícone tem 48px, e o painel herdar isso
     * espreme o conteúdo e cria rolagem horizontal. Nesse caso a largura vem da classe
     * (`w-96`, por exemplo).
     */
    matchAnchorWidth?: boolean
}

export function useAnchoredDropdown(
    open: boolean,
    onClose: () => void,
    options: AnchoredDropdownOptions = {},
) {
    const { matchAnchorWidth = true } = options
    const anchorRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const [style, setStyle] = useState<CSSProperties>({ display: 'none' })

    const reposition = useCallback(() => {
        const el = anchorRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        // Abre para cima quando não há espaço embaixo — numa tabela longa, a última
        // linha fica colada no rodapé da janela.
        const below = window.innerHeight - rect.bottom
        const openUp = below < 240 && rect.top > below
        setStyle({
            position: 'fixed',
            left: rect.left,
            ...(matchAnchorWidth ? { width: rect.width } : {}),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
            maxHeight: Math.max(160, (openUp ? rect.top : below) - 16),
            zIndex: 9999,
        })
    }, [matchAnchorWidth])

    useLayoutEffect(() => {
        if (!open) return
        reposition()
        window.addEventListener('scroll', reposition, true)
        window.addEventListener('resize', reposition)
        return () => {
            window.removeEventListener('scroll', reposition, true)
            window.removeEventListener('resize', reposition)
        }
    }, [open, reposition])

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (anchorRef.current?.contains(target)) return
            if (panelRef.current?.contains(target)) return
            onClose()
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open, onClose])

    /** Renderiza o painel no `body`, já posicionado. */
    const renderPanel = (children: ReactNode, className = '') =>
        open
            ? createPortal(
                  <div
                      ref={panelRef}
                      style={style}
                      className={`overflow-y-auto bg-base-100 border border-base-300 rounded-lg shadow-xl ${className}`}
                  >
                      {children}
                  </div>,
                  document.body
              )
            : null

    return {
        anchorRef: anchorRef as RefObject<HTMLDivElement>,
        renderPanel,
    }
}
